import React, { useState, useRef, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Calculator, TrendingUp } from 'lucide-react';

const ApexCalculator = () => {
  const navigate = useNavigate();
  const [consultations, setConsultations] = useState(40);
  const [closeRate, setCloseRate] = useState(30);
  const [membershipValue, setMembershipValue] = useState(150);
  const [animateResult, setAnimateResult] = useState(false);
  const timeoutRef = useRef(null);

  const goToApplication = () => {
    navigate('/5k-guarantee#application');
  };

  // Calculate current and projected revenue
  const currentMonthlySales = Math.round(consultations * (closeRate / 100));
  const currentMonthlyRevenue = currentMonthlySales * membershipValue;
  
  // 28% improvement applies to close rate improvement
  const improvedCloseRate = Math.min(100, closeRate * 1.28);
  const projectedMonthlySales = Math.round(consultations * (improvedCloseRate / 100));
  const projectedMonthlyRevenue = projectedMonthlySales * membershipValue;
  
  const monthlyIncrease = projectedMonthlyRevenue - currentMonthlyRevenue;
  const yearlyIncrease = monthlyIncrease * 12;

  // Trigger animation on value change
  const triggerAnimation = () => {
    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current);
    }
    setAnimateResult(true);
    timeoutRef.current = setTimeout(() => setAnimateResult(false), 300);
  };

  useEffect(() => {
    return () => {
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
      }
    };
  }, []);

  const handleConsultationsChange = (value) => {
    setConsultations(value);
    triggerAnimation();
  };

  const handleCloseRateChange = (value) => {
    setCloseRate(value);
    triggerAnimation();
  };

  const handleMembershipValueChange = (value) => {
    setMembershipValue(value);
    triggerAnimation();
  };

  const formatCurrency = (value) => {
    return new Intl.NumberFormat('en-GB', {
      style: 'currency',
      currency: 'GBP',
      maximumFractionDigits: 0
    }).format(value);
  };

  return (
    <section id="calculator" className="relative py-20 md:py-28 bg-[#0A0A0A]">
      {/* Background effects */}
      <div className="absolute inset-0 bg-gradient-to-br from-[#0A0A0A] via-[#0d1f12]/20 to-[#0A0A0A]" />
      <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[600px] h-[600px] bg-[#22C55E]/5 rounded-full blur-[150px]" />
      
      <div className="relative max-w-6xl mx-auto px-6 md:px-12 lg:px-24">
        {/* Section header */}
        <div className="text-center mb-12">
          <span className="text-[#22C55E] text-sm font-medium tracking-[0.2em] uppercase">
            Revenue Calculator
          </span>
          <h2 className="text-3xl md:text-4xl font-bold text-white mt-4">
            See Your Potential Growth
          </h2>
          <p className="text-[#A1A1AA] mt-4 max-w-2xl mx-auto">
            Enter your current numbers to see how a 28% improvement in your close rate 
            could transform your revenue — without spending more on ads.
          </p>
        </div>

        {/* Calculator card */}
        <div className="bg-[#121212] border border-[#27272A] rounded-3xl overflow-hidden">
          <div className="grid lg:grid-cols-2">
            {/* Input side */}
            <div className="p-8 md:p-10 space-y-8 border-b lg:border-b-0 lg:border-r border-[#27272A]">
              <div className="flex items-center gap-3">
                <div className="w-12 h-12 rounded-xl bg-[#22C55E]/10 flex items-center justify-center">
                  <Calculator className="w-6 h-6 text-[#22C55E]" />
                </div>
                <div>
                  <h3 className="text-white font-semibold text-lg">Your Current Numbers</h3>
                  <p className="text-[#71717A] text-sm">Adjust the sliders below</p>
                </div>
              </div>

              {/* Consultations input */}
              <div className="space-y-3">
                <div className="flex justify-between items-center">
                  <label className="text-white font-medium">Monthly Consultations</label>
                  <span className="text-[#22C55E] font-bold text-xl">{consultations}</span>
                </div>
                <input
                  type="range"
                  min="10"
                  max="200"
                  value={consultations}
                  onChange={(e) => handleConsultationsChange(Number(e.target.value))}
                  data-testid="calculator-input-consultations"
                  className="w-full h-2 bg-[#27272A] rounded-lg appearance-none cursor-pointer accent-[#22C55E]"
                  style={{
                    background: `linear-gradient(to right, #22C55E 0%, #22C55E ${((consultations - 10) / 190) * 100}%, #27272A ${((consultations - 10) / 190) * 100}%, #27272A 100%)`
                  }}
                />
                <p className="text-[#71717A] text-xs">Number of sales conversations per month</p>
              </div>

              {/* Close rate input */}
              <div className="space-y-3">
                <div className="flex justify-between items-center">
                  <label className="text-white font-medium">Current Close Rate</label>
                  <span className="text-[#22C55E] font-bold text-xl">{closeRate}%</span>
                </div>
                <input
                  type="range"
                  min="5"
                  max="80"
                  value={closeRate}
                  onChange={(e) => handleCloseRateChange(Number(e.target.value))}
                  data-testid="calculator-input-closerate"
                  className="w-full h-2 bg-[#27272A] rounded-lg appearance-none cursor-pointer accent-[#22C55E]"
                  style={{
                    background: `linear-gradient(to right, #22C55E 0%, #22C55E ${((closeRate - 5) / 75) * 100}%, #27272A ${((closeRate - 5) / 75) * 100}%, #27272A 100%)`
                  }}
                />
                <p className="text-[#71717A] text-xs">Percentage of consultations that convert</p>
              </div>

              {/* Membership value input */}
              <div className="space-y-3">
                <div className="flex justify-between items-center">
                  <label className="text-white font-medium">Avg. Membership Value</label>
                  <span className="text-[#22C55E] font-bold text-xl">{formatCurrency(membershipValue)}</span>
                </div>
                <input
                  type="range"
                  min="50"
                  max="500"
                  step="10"
                  value={membershipValue}
                  onChange={(e) => handleMembershipValueChange(Number(e.target.value))}
                  data-testid="calculator-input-value"
                  className="w-full h-2 bg-[#27272A] rounded-lg appearance-none cursor-pointer accent-[#22C55E]"
                  style={{
                    background: `linear-gradient(to right, #22C55E 0%, #22C55E ${((membershipValue - 50) / 450) * 100}%, #27272A ${((membershipValue - 50) / 450) * 100}%, #27272A 100%)`
                  }}
                />
                <p className="text-[#71717A] text-xs">Average monthly value per member</p>
              </div>
            </div>

            {/* Results side */}
            <div className="p-8 md:p-10 bg-gradient-to-br from-[#121212] to-[#0d1f12] relative">
              {/* Glow effect */}
              <div className="absolute top-0 right-0 w-32 h-32 bg-[#22C55E]/20 rounded-full blur-[60px]" />
              
              <div className="relative space-y-8">
                <div className="flex items-center gap-3">
                  <div className="w-12 h-12 rounded-xl bg-[#22C55E]/20 flex items-center justify-center">
                    <TrendingUp className="w-6 h-6 text-[#22C55E]" />
                  </div>
                  <div>
                    <h3 className="text-white font-semibold text-lg">Your Results</h3>
                    <p className="text-[#71717A] text-sm">With Apex coaching</p>
                  </div>
                </div>

                {/* Current vs Projected */}
                <div className="space-y-4">
                  <div className="bg-[#0A0A0A]/50 rounded-xl p-4 border border-[#27272A]">
                    <p className="text-[#71717A] text-sm mb-1">Current Monthly Revenue</p>
                    <p className="text-white text-2xl font-bold">{formatCurrency(currentMonthlyRevenue)}</p>
                    <p className="text-[#71717A] text-xs mt-1">{currentMonthlySales} sales × {formatCurrency(membershipValue)}</p>
                  </div>

                  <div className={`bg-[#22C55E]/10 rounded-xl p-4 border border-[#22C55E]/30 ${animateResult ? 'animate-pulse-glow' : ''}`}>
                    <p className="text-[#22C55E] text-sm mb-1">Projected Monthly Revenue</p>
                    <p className="text-white text-3xl font-bold">{formatCurrency(projectedMonthlyRevenue)}</p>
                    <p className="text-[#71717A] text-xs mt-1">{projectedMonthlySales} sales × {formatCurrency(membershipValue)}</p>
                  </div>
                </div>

                {/* Increase highlight */}
                <div className="bg-[#22C55E] rounded-xl p-6 text-center">
                  <p className="text-black/70 text-sm font-medium mb-1">Potential Annual Increase</p>
                  <p className={`text-black text-4xl font-bold ${animateResult ? 'animate-number-tick' : ''}`} data-testid="calculator-result-annual">
                    +{formatCurrency(yearlyIncrease)}
                  </p>
                  <p className="text-black/70 text-sm mt-2">
                    ({formatCurrency(monthlyIncrease)}/month extra)
                  </p>
                </div>

                {/* CTA */}
                <button
                  onClick={goToApplication}
                  data-testid="calculator-cta"
                  className="w-full bg-white hover:bg-gray-100 text-black font-bold uppercase tracking-wide rounded-full px-8 py-4 transition-all duration-300 hover:-translate-y-0.5"
                >
                  Claim Your Free Audit
                </button>
                <p className="text-center text-[#71717A] text-sm">
                  No obligation. See if we are available in your area.
                </p>
              </div>
            </div>
          </div>
        </div>

        {/* Disclaimer */}
        <p className="text-center text-[#71717A] text-xs mt-6 max-w-2xl mx-auto">
          * Based on our average client results of 28% improvement in close rates. 
          Individual results may vary based on implementation and market conditions.
        </p>
      </div>
    </section>
  );
};

export default ApexCalculator;
